import { type JSX, useEffect, useState } from "react"
import { type FileEntry, fetchFileText, fetchFiles, renameFile, saveFileText } from "./api"
import { MarkdownFile } from "./markdown_file"
import { shorten } from "./text"

// shared/public_findings/ — the important things to know about the problem.
// Like the literature, none of it is in our database: the sandbox's filesystem
// is the record, so this reads it live.
//
// This pane deliberately shows ONE of the sandbox's two findings directories.
// `shared/agent_findings/` is the working record — status notes, brainstorms,
// audits, session write-ups — which agents write freely and which reached 1,556
// files on one workspace. `shared/public_findings/` is the short answer to
// "what would someone picking up this problem need to know", and it is the only
// one a person should be handed. See controller/initialize.py in the sandbox
// for the bar the agents are held to.
//
// A workspace created before that split has its whole record in `shared/
// findings/` and nothing here, so this pane shows its empty state. That is
// correct rather than broken: nothing in an unsplit workspace was ever held to
// the public bar.
//
// Findings are markdown written straight into prose, usually with no front
// matter, so a document is named by its opening heading (the runtime's listing
// works that out — see controller/listing.py).

export const FINDINGS_PATH = "shared/public_findings"

// Every workspace is seeded with shared/public_findings/README.md — the
// conventions note telling agents how to write a finding. It is instructions to
// them, not a result for you, so it is left out of the listing rather than
// shown as the first document in the pane.
const FINDINGS_EXCLUDE = ["README.md"]

export function Findings({
  filesUrl,
  fileUrl,
  renameUrl,
  openPath,
  onOpenFinding,
}: {
  filesUrl: string
  fileUrl: string
  renameUrl: string
  // Which finding is open, if any. Held by the app so the rail can close it by
  // navigating to Findings with nothing open.
  openPath?: string
  onOpenFinding: (path: string | undefined) => void
}): JSX.Element {
  const [entries, setEntries] = useState<FileEntry[] | null>(null)
  const [failed, setFailed] = useState(false)
  // Newest first, because a findings list is read to see what just landed —
  // the document you want is almost always the one an agent published last.
  const [sort, setSort] = useState<Sort>({ key: "updated", direction: "desc" })

  useEffect(() => {
    let cancelled = false

    void fetchFiles(filesUrl, FINDINGS_PATH, FINDINGS_EXCLUDE).then((listing) => {
      if (cancelled) return
      if (listing) setEntries(listing.entries)
      else setFailed(true)
    })

    return () => {
      cancelled = true
    }
  }, [filesUrl])

  const documents = sortDocuments((entries ?? []).filter((entry) => entry.kind === "file"), sort)
  const open = documents.find((entry) => entry.path === openPath)

  if (open) {
    return (
      <Finding
        entry={open}
        fileUrl={fileUrl}
        renameUrl={renameUrl}
        onBack={() => onOpenFinding(undefined)}
        onRenamed={(path, name) => {
          // The listing is stale the moment a file changes name, and the open
          // document is addressed by its path — so fix both rather than send
          // the reader back to a list that still shows the old name.
          setEntries((current) =>
            current?.map((entry) => (entry.path === open.path ? { ...entry, path, name } : entry)) ?? current)
          onOpenFinding(path)
        }}
      />
    )
  }

  return (
    <article className="workspace__doc workspace__doc--wide">
      <header className="workspace__doc-head">
        <h1 className="workspace__doc-title">Findings</h1>
        <p className="workspace__doc-meta">
          {failed
            ? "Could not reach this workspace's sandbox."
            : entries === null
              ? "Loading…"
              : documents.length === 0
                ? "Nothing yet. Agents publish what they find here."
                : `${documents.length} ${documents.length === 1 ? "document" : "documents"} agents have published.`}
        </p>
      </header>

      {documents.length > 0 && (
        <div className="workspace__table-shell">
          <table className="workspace__table">
            <thead>
              <tr>
                <SortableHeader label="Title" sortKey="title" sort={sort} onSort={setSort} />
                <th scope="col">Contents</th>
                <th scope="col">File</th>
                <SortableHeader label="Updated" sortKey="updated" sort={sort} onSort={setSort} />
              </tr>
            </thead>
            <tbody>
              {documents.map((entry) => (
                <tr key={entry.path} onClick={() => onOpenFinding(entry.path)}>
                  <td>
                    <button type="button" className="workspace__table-open">
                      {entry.title || entry.name}
                    </button>
                  </td>
                  <td className="workspace__table-summary">
                    {entry.preview ? shorten(entry.preview) : "—"}
                  </td>
                  <td className="workspace__table-venue">{entry.name}</td>
                  <td className="workspace__table-published">{formatWhen(entry.modified_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  )
}

// How the list is ordered. Two columns are worth sorting by: what a document is
// called, and when it last changed. The others are a preview and a filename —
// neither is something anyone looks for a document by.
type SortKey = "title" | "updated"
type Sort = { key: SortKey; direction: "asc" | "desc" }

// What each column means when you first click it: names read A-Z, dates read
// newest first. Clicking the column you are already on reverses it instead.
const NATURAL_DIRECTION: Record<SortKey, Sort["direction"]> = { title: "asc", updated: "desc" }

function nextSort(current: Sort, key: SortKey): Sort {
  if (current.key !== key) return { key, direction: NATURAL_DIRECTION[key] }
  return { key, direction: current.direction === "asc" ? "desc" : "asc" }
}

function sortDocuments(documents: FileEntry[], sort: Sort): FileEntry[] {
  const ordered = [ ...documents ].sort((a, b) => compare(a, b, sort.key))
  return sort.direction === "asc" ? ordered : ordered.reverse()
}

function compare(a: FileEntry, b: FileEntry, key: SortKey): number {
  if (key === "title") {
    // The same string the row shows: a finding with no heading is listed by
    // its filename, so that is what it has to sort by too.
    return titleOf(a).localeCompare(titleOf(b), undefined, { sensitivity: "base", numeric: true })
  }

  // A listing from an older runtime carries no timestamps, and a date that
  // will not parse is the same problem. Both count as the oldest thing there
  // is, which puts them at the bottom of the default view — subtracting NaN
  // instead would compare equal to everything and leave them wherever the
  // listing happened to put them.
  return at(a) - at(b)
}

function at(entry: FileEntry): number {
  const parsed = Date.parse(entry.modified_at ?? "")
  return Number.isNaN(parsed) ? 0 : parsed
}

function titleOf(entry: FileEntry): string {
  return entry.title || entry.name
}

// A column header you can sort by. `aria-sort` is what tells a screen reader
// the table is ordered and which way, so it goes on the th rather than the
// button inside it.
function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string
  sortKey: SortKey
  sort: Sort
  onSort: (sort: Sort) => void
}): JSX.Element {
  const active = sort.key === sortKey
  const ascending = sort.direction === "asc"

  return (
    <th scope="col" aria-sort={active ? (ascending ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className={`workspace__table-sort${active ? " is-active" : ""}`}
        onClick={() => onSort(nextSort(sort, sortKey))}
      >
        {label}
        {/* Only the sorted column carries an arrow. A row of them on every
            header reads as decoration and stops saying anything. */}
        {active && <span aria-hidden="true">{ascending ? "↑" : "↓"}</span>}
      </button>
    </th>
  )
}

// One finding, opened in the same editor as the objective: rendered markdown,
// click to edit the raw text, Save writes it back. Saving means writing into
// the sandbox (cmd.file.write), which is an overwrite by atomic rename — an
// agent reading this file concurrently sees the old contents or the new ones,
// never half of either.
function Finding({
  entry,
  fileUrl,
  renameUrl,
  onBack,
  onRenamed,
}: {
  entry: FileEntry
  fileUrl: string
  renameUrl: string
  onBack: () => void
  onRenamed: (path: string, name: string) => void
}): JSX.Element {
  const [text, setText] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    void fetchFileText(fileUrl, entry.path).then((body) => {
      if (cancelled) return
      if (body === null) setFailed(true)
      else setText(body)
    })

    return () => {
      cancelled = true
    }
  }, [fileUrl, entry.path])

  // The header is ours, not the author's: it is how the workspace records what
  // a document is, and a person editing prose has no reason to be handed a
  // block of `key: value` — or to be able to break it. So only the body is
  // shown and edited, and the header is put back on the way out exactly as it
  // came, never re-rendered.
  const [header, body] = splitFrontMatter(text ?? "")

  if (failed) {
    return (
      <article className="workspace__doc">
        <header className="workspace__doc-head">
          <h1 className="workspace__doc-title">
            <button type="button" className="workspace__back" onClick={onBack} aria-label="Back to findings">
              ←
            </button>
            {entry.title || entry.name}
          </h1>
        </header>
        <p className="workspace__field-hint">Could not read this document from the sandbox.</p>
      </article>
    )
  }

  return (
    <MarkdownFile
      // Remounts when you open a different finding, so the editor never shows
      // one document's draft over another's text.
      key={entry.path}
      // The filename heads the document, not the title. A finding's title is
      // its own first heading, which is right there in the prose below — and
      // the name is the thing that identifies it on disk, to agents and to
      // every citation of it.
      name={entry.name}
      meta={
        text === null
          ? "Loading…"
          : [ entry.title, `Updated ${formatWhen(entry.modified_at)}` ].filter(Boolean).join(" · ")
      }
      body={body}
      onBack={onBack}
      onRename={async (next) => {
        const result = await renameFile(renameUrl, entry.path, next)
        if (typeof result === "string") return result
        onRenamed(result.path, next)
        return null
      }}
      onSave={(next) => saveFileText(fileUrl, entry.path, header + next)}
    />
  )
}

// [header, body]. The header keeps its exact bytes — reformatting metadata
// nobody asked to touch is how a round trip quietly loses a field.
function splitFrontMatter(text: string): [string, string] {
  const match = /^---[ \t]*\n[\s\S]*?\n---[ \t]*\n?/.exec(text)
  return match ? [ match[0], text.slice(match[0].length) ] : [ "", text ]
}

function formatWhen(iso: string | undefined): string {
  if (!iso) return "—"
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? "—" : at.toLocaleString()
}
